<template>
  <div class="card overflow-hidden">
    <div class="shadow">
      <HighlightedText
        :text="$t(header)"
        tag="h3"
        class="text-center px-3 pt-3 pb-2 text-uppercase"
      />

      <ul
        class="nav nav-tabs nav-fill"
        role="tablist"
      >
        <li
          v-for="gender in ['m','f']"
          :key="gender"
          class="nav-item"
        >
          <a
            :class="['nav-link', gender === filter && 'active']"
            role="button"
            href="#"
            @click.prevent="filter = gender"
          >
            {{ $t(`liveLeaderboard.${gender}`) }}
          </a>
        </li>
      </ul>
    </div>

    <div
      class="card-body pt-0"
      style="min-height: 4rem;"
    >
      <Loading
        v-if="!runners.length"
        class="mt-4 mb-3"
      />
      <transition-group
        v-else
        tag="ol"
        name="fade"
        :class="$style.list"
        appear
      >
        <li
          v-for="({ id, fullname, url, location, status, /* gender, ageGroup, */ distance, nationality }, i) in list"
          :key="id"
          :title="`${fullname} - ${$formatDistance((distance / 1000).toFixed(3).slice(0, -2))}`"
          :class="['py-3 position-relative', isRunning(status) && $style.running]"
        >
          <icon
            v-if="rankBadgeMap[i + 1]"
            :name="rankBadgeMap[i + 1]"
            :class="$style['rank-icon']"
          />
          <div
            v-if="i + 1 > 3"
            :class="[$style['pill-number']]"
            v-text="i + 1"
          />
          <Avatar
            :runner="list[i]"
            sm
            class="mr-2"
          />

          <div>
            <h5 class="mb-0 text-uppercase font-sm">
              <router-link
                :to="url"
                :class="$style['dark-text']"
              >
                {{ fullname }}
              </router-link>
            </h5>
            <small class="d-block font-weight-bold text-condensed dark-text">
              <template v-if="nationality">
                <flag
                  :iso="nationality"
                  :squared="false"
                  class="mr-1"
                />
                {{ getIoc(nationality) }}
              </template>
              <template v-else-if="location">
                <flag
                  :iso="location.iso2"
                  :squared="false"
                  class="mr-1"
                />
                {{ location.ioc }}
              </template>
            </small>
          </div>
          <div
            v-if="hasFeature('liveLeaderboardDistances')"
            class="ml-auto h5"
            :class="$style['dark-subtle-text']"
          >
            {{
              $formatDistance((distance / 1000).toFixed(3)
                .slice(0, -2))
            }}
          </div>
        </li>
      </transition-group>

      <div class="text-center">
        <button
          v-if="paginate"
          class="btn btn-outline-primary w-100"
          type="button"
          @click="$emit('next')"
        >
          {{ $tc('list.more', 1) }}
        </button>
        <button
          v-else-if="$runners.length > minItems"
          class="btn btn-outline-primary w-100"
          type="button"
          @click="collapsed = !collapsed"
        >
          {{ $tc('list.more', collapsed) }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Avatar from '@/components/Avatar.vue'
import HighlightedText from '@/components/HighlightedText.vue'
import Loading from '@/components/Loading.vue'
import { mapGetters } from 'vuex'
import getIoc from '@/util/ioc'
import { status } from '@/constants/participant'
import hasStatusBadge from '@/util/hasStatusBadge'

const rankBadgeMap = {
  1: 'RankGold',
  2: 'RankSilver',
  3: 'RankBronze'
}

export default {
  name: 'LiveLeaderboard',
  components: {
    Avatar,
    HighlightedText,
    Loading
  },
  props: {
    header: {
      type: String,
      default: 'liveLeaderboard.header'
    },
    runners: {
      type: Array,
      required: true
    },
    minItems: {
      type: Number,
      default: 5
    },
    paginate: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      rankBadgeMap,
      filter: 'm',
      collapsed: true
    }
  },
  computed: {
    ...mapGetters([
      'hasFeature'
    ]),
    $runners() {
      return this.runners
        .filter(runner => runner.gender === this.filter)
        .sort((a, b) => b.distance - a.distance || a.fullname.localeCompare(b.fullname))
    },
    list() {
      if (this.collapsed) {
        return this.$runners.slice(0, this.minItems)
      }

      return this.$runners
    }
  },
  methods: {
    getIoc,
    isRunning() {
      return status === status.RUNNING
    },
    hasStatusBadge
  }
}
</script>

<style lang="scss" module>
@import "~@/scss/bootstrap/custom";

.rank-icon{
  margin-right: map-get($spacers, 3);
  width: 1.5rem;
  height: 1.75rem;
}
.pill-number {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: map-get($spacers, 3);
  font-weight: $font-weight-bold;
  font-stretch: condensed;
  width: 1.5rem;
  height: 1.75rem;
}

.list {
  padding-left: 0;
  margin-bottom: 0;
  counter-reset: top-ten;

  > li {
    display: flex;
    align-items: center;
    counter-increment: top-ten;
    list-style-type: none;

    & + li {
      //margin-top: map-get($spacers, 2);
      border-top: $border-width solid $gray-300;
    }

    &.running {
      &::before {
        background-color: theme-color('secondary');
      }
    }
  }
}
.dark-text{
  color: $text-dark;
}

.dark-subtle-text{
  color: $text-dark-subtle;
}
</style>
